import { useEffect, useState } from 'react';
import * as React from 'react';
import clsx from 'clsx';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { TdkAutocompleteProps } from './tdk-auto-complete-interface';
import parse from 'autosuggest-highlight/parse';
import { match } from './match';
import { useStyles } from './tdk-auto-complete.style';
import { FilterOptionsState, UseAutocompleteProps } from '@material-ui/lab/useAutocomplete';
import { StandardProps } from '@material-ui/core';
import { AutocompleteClassKey, AutocompleteRenderOptionState } from '@material-ui/lab/Autocomplete/Autocomplete';

const unAcceptableChars = /[^a-zA-Z0-9æøåÆØÅ\s]+/gu;
export const sanitizeInput = (input: string) => {
    return input.replace(unAcceptableChars, '');
};

export const filterFunction = (options: any[], state: FilterOptionsState<any>) => {
    const inputValueFiltered = sanitizeInput(state.inputValue);
    const r = new RegExp(inputValueFiltered, 'iu');
    return options.filter((v) => r.test(v.text) || v._always);
};

const TdkAutoComplete = ({
    highlight,
    highlightKey,
    required = false,
    disabled = false,
    errorTextInvalid = 'Der er fejl i det indtastede',
    startShowing = 2,
    isSubmitted = false,
    customClass,
    label,
    ...rest
}: TdkAutocompleteProps<any> &
    UseAutocompleteProps<any, any, any, any> &
    StandardProps<
        React.HTMLAttributes<HTMLDivElement>,
        AutocompleteClassKey,
        'defaultValue' | 'onChange' | 'children'
    >) => {
    const classes = useStyles();
    const [open, setOpen] = useState<boolean>(false);
    const customClassNames = {
        ...rest.classes,
        inputRoot: classes.inputWrapper,
        input: classes.inputField,
        listbox: classes.listBox,
        option: classes.options,
        paper: classes.paper,
    };

    useEffect(() => {
        if (highlight && (!highlightKey || highlightKey === '')) {
            console.error('please provide a valid highlightKey');
        }
    }, [highlight, highlightKey]);

    let renderOptionsObject = {};
    if (highlight && highlightKey && highlightKey !== '') {
        const renderOptionFunc = (option: any, { inputValue }: AutocompleteRenderOptionState) => {
            const matches = match(option[highlightKey as string], inputValue);
            const parts = parse(option[highlightKey as string], matches);

            return (
                <div>
                    {parts.map((part, index) => (
                        <span key={index} className={clsx(part.highlight && classes.textHighlight)}>
                            {part.text}
                        </span>
                    ))}
                </div>
            );
        };
        renderOptionsObject = { renderOption: renderOptionFunc };
    }

    return (
        <Autocomplete
            onInputChange={(_e, v, r) => {
                if (r === 'input') {
                    if (v.length > startShowing) {
                        setOpen(true);
                    } else {
                        setOpen(false);
                    }
                }
                if (r === 'reset') {
                    setOpen(false);
                }
            }}
            onChange={(_e, _v, r, _d) => {
                if (r === 'select-option') {
                    setOpen(false);
                }
            }}
            onClose={() => {}}
            onOpen={() => {}}
            open={open}
            classes={customClassNames}
            forcePopupIcon={false}
            className={clsx(customClass ? `${customClass} ${classes.container}` : classes.container)}
            filterOptions={filterFunction}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    error={isSubmitted}
                    helperText={isSubmitted ? errorTextInvalid : undefined}
                />
            )}
            {...renderOptionsObject}
            {...rest}
        />
    );
};

export { TdkAutoComplete };
export type { TdkAutocompleteProps };
